<template>
  <ts-loading-banner :loading="loading">
    <div class="ts-space-y-3">
      <div class="row">
        <div class="col-md-6">
          <label class="ts-text-xs text-muted">{{
            $t('allowanceClaimMission.totalAllowance')
          }}</label>
          <input
            :value="model.total_allowance"
            type="text"
            class="form-control"
            readonly
          />
        </div>
        <div class="col-md-6">
          <label class="ts-text-xs text-muted">{{
            $t('allowanceClaimMission.advanceAmount')
          }}</label>
          <input
            :value="model.advance_amount"
            type="text"
            class="form-control"
            readonly
          />
        </div>
      </div>

      <div class="row">
        <div class="col-md-6">
          <label class="ts-text-xs">{{
            $t('allowanceClaimMission.deductionAmount')
          }}</label>
          <input
            v-model.number="model.amount_deduction"
            type="number"
            class="form-control"
            @input="recalculate"
          />
        </div>
        <div class="col-md-6">
          <label class="ts-text-xs">{{
            $t('allowanceClaimMission.amountToBeClaimed')
          }}</label>
          <input
            v-model.number="model.amount_claimed"
            type="number"
            class="form-control"
          />
        </div>
      </div>

      <div class="row">
        <div class="col-md-6">
          <label class="ts-text-xs text-muted">{{
            $t('allowanceClaimMission.paymentDue')
          }}</label>
          <input
            :value="capitalize(model.payment_due)"
            type="text"
            class="form-control"
            readonly
          />
        </div>
      </div>

      <div class="row">
        <div class="col-md-12">
          <label class="ts-text-xs">{{
            $t('allowanceClaimMission.remark')
          }}</label>
          <textarea
            v-model="model.claim_remark"
            class="form-control"
            rows="3"
          ></textarea>
        </div>
      </div>

      <!-- Slip Upload -->
      <div class="row">
        <div class="col-md-12">
          <label class="ts-text-xs">{{
            $t('allowanceClaimMission.attachSlip')
          }}</label>
          <div class="d-flex flex-column align-items-start">
            <div v-if="slipPreview" class="mb-2">
              <img
                :src="slipPreview"
                style="max-height: 120px; max-width: 200px; object-fit: contain; border: 1px solid #ddd; border-radius: 4px;"
              />
            </div>
            <input
              type="file"
              ref="slipInput"
              accept="image/*"
              style="display:none"
              @change="onSlipChange"
            />
            <div class="d-flex ts-space-x-2">
              <ts-button @click.prevent="$refs.slipInput.click()">
                <i class="fas fa-upload mr-1"></i>
                {{
                  slipPreview
                    ? $t('allowanceClaimMission.changeFile')
                    : $t('allowanceClaimMission.uploadFile')
                }}
              </ts-button>
              <ts-button
                v-if="slipPreview"
                color="danger"
                @click.prevent="removeSlip"
              >
                <i class="fas fa-times mr-1"></i>
                {{ $t('allowanceClaimMission.remove') }}
              </ts-button>
            </div>
          </div>
        </div>
      </div>

      <div class="row ts-mt-3">
        <div class="col-md-12 text-right ts-space-x-2">
          <ts-button @click.prevent="$emit('cancel')">{{
            $t('cancel')
          }}</ts-button>
          <ts-button
            color="primary"
            :waiting="waiting"
            :disabled="waiting"
            @click.prevent="onUpdate"
          >
            {{ $t('update') }}
          </ts-button>
        </div>
      </div>
    </div>
  </ts-loading-banner>
</template>

<script>
export default {
  name: 'ClaimEditForm',
  props: {
    mission: { type: Object, required: true }
  },
  data() {
    return {
      loading: false,
      waiting: false,
      slipFile: null,
      slipPreview: this.mission.attach_slip || null,
      slipRemoved: false,
      model: {
        total_allowance: this.mission.total_allowance || 0,
        advance_amount: this.mission.advance_amount || 0,
        amount_deduction: this.mission.amount_deduction || 0,
        amount_claimed: this.mission.amount_claimed || 0,
        payment_due: this.mission.payment_due || null,
        claim_remark: this.mission.claim_remark || null
      }
    }
  },
  watch: {
    'model.amount_claimed'(val) {
      this.model.payment_due = val > 0 ? 'employee' : 'company'
    }
  },
  methods: {
    recalculate() {
      this.model.amount_claimed =
        this.model.total_allowance -
        this.model.advance_amount -
        this.model.amount_deduction
      this.model.payment_due =
        this.model.amount_claimed > 0 ? 'employee' : 'company'
    },
    capitalize(val) {
      if (!val) return ''
      return val.charAt(0).toUpperCase() + val.slice(1)
    },
    onSlipChange(event) {
      const file = event.target.files[0]
      if (!file) return
      this.slipFile = file
      this.slipPreview = URL.createObjectURL(file)
    },
    removeSlip() {
      this.slipFile = null
      this.slipPreview = null
      this.slipRemoved = true
      this.$refs.slipInput.value = ''
    },
    async onUpdate() {
      this.waiting = true
      try {
        let payload

        if (this.slipFile) {
          payload = new FormData()
          payload.append('amount_deduction', this.model.amount_deduction)
          payload.append('amount_claimed', this.model.amount_claimed)
          payload.append('payment_due', this.model.payment_due)
          payload.append('claim_remark', this.model.claim_remark || '')
          payload.append('remove_slip', '0')
          payload.append('attach_slip', this.slipFile)
        } else {
          payload = {
            amount_deduction: this.model.amount_deduction,
            amount_claimed: this.model.amount_claimed,
            payment_due: this.model.payment_due,
            claim_remark: this.model.claim_remark || '',
            remove_slip: this.slipRemoved ? '1' : '0'
          }
        }

        await this.$store.dispatch('mission/missionClaim/update', {
          missionId: this.mission.id,
          data: payload
        })

        this.$toasted.success('Updated successfully')
        this.$emit('fetchData')
        this.$emit('cancel')
      } catch (e) {
        this.$toasted.error(e.message)
      } finally {
        this.waiting = false
      }
    }
  }
}
</script>
